// Material-UI版 従業員一覧テーブル

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { CompanyEmployee } from '../../types/employee';
import employeeService from '../../services/employeeService';

interface EmployeeTableMUIProps {
  employees: CompanyEmployee[];
  onSort: (sortBy: 'name' | 'created_at') => void;
  onViewDetail: (employee: CompanyEmployee) => void;
  onEdit: (employee: CompanyEmployee) => void;
  sortBy?: 'name' | 'created_at';
  sortOrder?: 'asc' | 'desc';
}

export const EmployeeTableMUI: React.FC<EmployeeTableMUIProps> = ({
  employees,
  onSort,
  onViewDetail,
  onEdit,
  sortBy,
  sortOrder,
}) => {
  // Phase 2: HRロールの色を追加
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'company_admin':
        return 'primary';
      case 'hr':
        return 'secondary'; // HRは青系のセカンダリーカラー
      case 'employee':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  if (employees.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
          従業員が見つかりません
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          条件に一致する従業員がいないか、まだ従業員が追加されていません。
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>従業員情報</TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === 'name'}
                direction={sortBy === 'name' ? sortOrder : 'asc'}
                onClick={() => onSort('name')}
              >
                氏名
              </TableSortLabel>
            </TableCell>
            <TableCell>部署・役職</TableCell>
            <TableCell>権限</TableCell>
            <TableCell>ステータス</TableCell>
            <TableCell>最終ログイン</TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === 'created_at'}
                direction={sortBy === 'created_at' ? sortOrder : 'asc'}
                onClick={() => onSort('created_at')}
              >
                登録日
              </TableSortLabel>
            </TableCell>
            <TableCell>操作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {employees.map((employee) => (
            <TableRow key={employee.id} hover>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ 
                    mr: 2, 
                    bgcolor: employee.role === 'company_admin' ? 'primary.main' : 
                            employee.role === 'hr' ? 'secondary.main' : 
                            'grey.500' 
                  }}>
                    {employee.role === 'company_admin' ? (
                      <CheckCircleIcon />
                    ) : employee.role === 'hr' ? (
                      <WorkIcon />
                    ) : (
                      <PersonIcon />
                    )}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {employee.user.fullName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      {employee.user.email}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {employee.user.fullName}
                </Typography>
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2">
                    {employee.department || '未設定'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                  >
                    {employee.jobTitle || '役職未設定'}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={employeeService.getRoleLabel(employee.role)}
                  color={getRoleColor(employee.role) as any}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={employeeService.getStatusLabel(employee.status)}
                  color={getStatusColor(employee.status) as any}
                  size="small"
                  variant={employee.status === 'active' ? 'filled' : 'outlined'}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {employeeService.formatLastLogin(employee.user.lastLoginAt)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {employeeService.formatCreatedAt(employee.createdAt)}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => onViewDetail(employee)}
                    sx={{
                      '&:hover': { bgcolor: 'primary.light', color: 'white' },
                    }}
                    title="詳細表示"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={() => onEdit(employee)}
                    sx={{
                      '&:hover': { bgcolor: 'info.light', color: 'white' },
                    }}
                    title="編集"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
